"use client"

import { useDebouncedValue } from "@/app/hooks/use-debounced-value"
import type { Currency } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import { Card, Input, Label, cn } from "@rarible_int/anaconda_ui_components"
import { useCallback, useEffect, useState } from "react"
import { CurrencySelect } from "./currency-select"

interface Props {
  initialPrice?: number
  onPriceChange(price: number | undefined): void
  error?: React.ReactNode
  caption?: React.ReactNode
  currency: Currency
  Shortcuts?: React.ComponentType<{ setPrice: (price: number) => void }>
}

export const MIN_PRICE = 0.0001

export function PriceInput({
  initialPrice,
  onPriceChange,
  error,
  caption,
  currency,
  Shortcuts,
}: Props) {
  const [inputValue, setInputValue] = useState(initialPrice)
  const price = useDebouncedValue(inputValue)

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (value === "") {
      setInputValue(undefined)
      return
    }

    const next = Number(value)
    // NaN/Infinity can appear while typing invalid intermediate states in <input type="number">.
    // Store undefined so downstream Big.js formatting doesn't throw.
    setInputValue(Number.isFinite(next) ? next : undefined)
  }, [])

  useEffect(() => {
    onPriceChange(price)
  }, [price, onPriceChange])

  return (
    <>
      <Card
        className={cn(
          "flex flex-col gap-3 rounded-lg p-4 has-[:focus-visible]:border-brand",
          error && "[&&]:border-destructive",
        )}
      >
        <Label htmlFor="price">
          <Trans>Price</Trans>
        </Label>
        <div className="flex min-w-0 items-center justify-between gap-2">
          {/* A number input carries a wide intrinsic width (size=20) and will
              not shrink below it without min-w-0, which pushed this row past
              the 396px offer dialog and clipped the whole form. */}
          <Input
            data-testid="price-input"
            value={inputValue ?? ""}
            onChange={handleInput}
            type="number"
            min={MIN_PRICE}
            step={"any"}
            className="min-w-0 flex-1 p-0 text-2xl text-headline caret-brand [appearance:textfield] [&&]:border-0 [&&]:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            placeholder="0.00"
          />
          <div className="shrink-0">
            <CurrencySelect currency={currency} />
          </div>
        </div>
        {Shortcuts ? <Shortcuts setPrice={setInputValue} /> : null}
      </Card>
      <div
        className={cn(
          "px-4 py-2 text-xs empty:hidden",
          error ? "text-destructive" : "text-white/60",
        )}
      >
        {error ? error : caption}
      </div>
    </>
  )
}
